


















































































































import axios from 'axios';
import Vue from 'vue';
import i18n from 'vue-i18n';
import { v4 as uuidv4 } from 'uuid';
import EventBus from '../../../eventBus';
import CoIcon from '../../Atoms/co-icon/CoIcon.vue';
import CoHeadline from '../../Atoms/co-headline/CoHeadline.vue';
import CoText from '../../Atoms/co-text/CoText.vue';
import CoForm from '../../Molecules/co-form/CoForm.vue';
import CoOtpVerification from '../../Molecules/co-otp-verification/CoOtpVerification.vue';
import CoModal from '../co-modal/CoModal.vue';

Vue.use(i18n);

const FORM_OTP_REQUIRED = 'FORM_OTP_REQUIRED';
const OTP_RESEND_COOLDOWN_MS = 60000;

interface WithdrawalPayload {
    name: string;
    email: string;
    invoiceNumber: string;
    language: string;
    // eslint-disable-next-line camelcase -- The public gateway contract uses this wire name.
    captcha_payload: string;
}

export default {
    name: 'CoRightOfWithdrawalForm',
    components: {
        CoForm,
        CoHeadline,
        CoIcon,
        CoModal,
        CoOtpVerification,
        CoText,
    },
    data() {
        return {
            formKey: 0,
            formFields: [
                {
                    Label: this.$t('labels.name'),
                    Type: 'text',
                    Name: 'name',
                    Value: '',
                    Required: true,
                    Placeholder: this.$t('placeholders.entername'),
                    ValidateOnMount: false,
                },
                {
                    Label: this.$t('labels.contactEmail'),
                    Type: 'email',
                    Name: 'email',
                    Value: '',
                    Required: true,
                    Placeholder: this.$t('placeholders.email'),
                    validationHint: this.$t('messages.withdrawalEmailHint'),
                    validationRegExp: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    ValidateOnMount: false,
                },
                {
                    Label: this.$t('labels.invoiceNumber'),
                    Type: 'text',
                    Name: 'invoiceNumber',
                    Value: '',
                    Required: true,
                    Placeholder: this.$t('placeholders.invoiceNumber'),
                    ValidateOnMount: false,
                },
            ],
            loading: false,
            pendingWithdrawalPayload: null as WithdrawalPayload | null,
            otpModalVisible: false,
            otpLoading: false,
            otpResendLoading: false,
            otpStatusMessage: '',
            otpResendAvailableAt: 0,
        };
    },
    computed: {
        otpBusy() {
            return this.otpLoading || this.otpResendLoading;
        },
    },
    methods: {
        resetForm() {
            this.formKey += 1;
            // The successful submission replaces the original trigger, so focus the fresh form.
            this.$nextTick(() => this.$refs.withdrawalForm?.$el.querySelector('input')?.focus());
        },
        getErrorCode(error) {
            return error?.response?.data?.code || '';
        },
        getPublicAxiosClient() {
            // Public forms must not send stale auth headers, otherwise the global auth redirect can send guests to login.
            const publicAxiosClient = axios.create({ baseURL: axios.defaults.baseURL || 'https://api.coapp.io/v1/' });
            delete publicAxiosClient.defaults.headers.common.Authorization;
            // Isolated clients do not inherit the global interceptor, so preserve request traceability explicitly.
            publicAxiosClient.interceptors.request.use((config) => ({
                ...config,
                headers: {
                    ...config.headers,
                    'X-Request-ID': uuidv4(),
                },
            }));
            return publicAxiosClient;
        },
        openOtpModal() {
            this.otpModalVisible = true;
            this.$nextTick(() => {
                // Submission temporarily disables its trigger; restore it explicitly on dismissal.
                this.$refs.otpModal?.show(this.$refs.withdrawalForm?.$el.querySelector('button[type="submit"]'));
                this.$nextTick(() => this.$refs.otpVerification?.focus());
            });
        },
        closeOtpModal() {
            if (!this.otpBusy) {
                this.$refs.otpModal?.hide();
            }
        },
        handleOtpModalClosed() {
            this.otpModalVisible = false;
            this.pendingWithdrawalPayload = null;
            this.otpStatusMessage = '';
            this.otpResendAvailableAt = 0;
        },
        resetHumanVerification() {
            this.$nextTick(() => this.$refs.withdrawalForm?.resetHumanVerification());
        },
        handleExpiredCaptcha() {
            if (this.otpModalVisible) {
                this.$refs.otpModal?.hide();
            }
            this.resetHumanVerification();
            EventBus.$emit('ERROR', {
                Message: this.$t('messages.otpCaptchaExpired'),
            });
        },
        getOtpErrorMessage(code: string, resend = false) {
            if (code === 'FORM_OTP_INVALID') {
                return this.$t('messages.emailconfirmation.invalidcode');
            }
            if (code === 'FORM_RATE_LIMITED') {
                return this.$t('messages.otpRateLimited');
            }
            if (code === 'FORM_PROCESSING_FAILED') {
                return this.$t('messages.withdrawalRequestError');
            }
            if (resend || code === 'FORM_OTP_SEND_FAILED') {
                return this.$t('messages.otpResendFailed');
            }
            return this.$t('messages.otpVerificationFailed');
        },
        clearOtpStatus() {
            this.otpStatusMessage = '';
        },
        async submitWithdrawalRequest(formData) {
            const payload = {
                name: formData.name,
                email: formData.email,
                invoiceNumber: formData.invoiceNumber,
                language: String(this.$i18n.locale || '')
                    .toLowerCase()
                    .startsWith('de')
                    ? 'de-DE'
                    : 'en-US',
                captcha_payload: formData.captcha_payload,
            };

            this.loading = true;
            try {
                const response = await this.getPublicAxiosClient().post('/forms/purchase-withdrawal-request', payload);
                if (response?.data?.code !== FORM_OTP_REQUIRED) {
                    throw new Error('Withdrawal verification response was not recognized');
                }

                // The gateway requires the complete public payload again when the email code is verified.
                this.pendingWithdrawalPayload = payload;
                this.otpResendAvailableAt = Date.now() + OTP_RESEND_COOLDOWN_MS;
                this.openOtpModal();
            } catch (error) {
                if (this.getErrorCode(error) === 'FORM_CAPTCHA_INVALID') {
                    this.resetHumanVerification();
                }
                EventBus.$emit('ERROR', {
                    Message: this.$t('messages.withdrawalRequestError'),
                    Details: error?.response?.data?.message || '',
                });
            } finally {
                this.loading = false;
            }
        },
        async verifyOtp(otp: string) {
            if (!this.pendingWithdrawalPayload || this.otpLoading) {
                return;
            }

            this.otpLoading = true;
            this.otpStatusMessage = '';
            try {
                await this.getPublicAxiosClient().post('/forms/purchase-withdrawal-request', {
                    ...this.pendingWithdrawalPayload,
                    otp,
                });

                this.$refs.otpModal?.hide();
                this.resetForm();
                EventBus.$emit('INFO', {
                    Message: this.$t('messages.withdrawalRequestSuccess'),
                });
            } catch (error) {
                const code = this.getErrorCode(error);
                if (code === 'FORM_CAPTCHA_INVALID') {
                    this.handleExpiredCaptcha();
                    return;
                }
                // OTP failures use the global toast pattern while the reusable input is reset for another attempt.
                EventBus.$emit('ERROR', {
                    Message: this.getOtpErrorMessage(code),
                });
                this.$refs.otpVerification?.resetCode();
            } finally {
                this.otpLoading = false;
            }
        },
        async resendOtp() {
            if (!this.pendingWithdrawalPayload || this.otpResendLoading) {
                return;
            }

            this.otpResendLoading = true;
            this.otpStatusMessage = '';
            try {
                const response = await this.getPublicAxiosClient().post(
                    '/forms/purchase-withdrawal-request',
                    this.pendingWithdrawalPayload
                );
                if (response?.data?.code !== FORM_OTP_REQUIRED) {
                    throw new Error('Withdrawal resend response was not recognized');
                }

                this.otpResendAvailableAt = Date.now() + OTP_RESEND_COOLDOWN_MS;
                this.otpStatusMessage = this.$t('messages.otpCodeResent');
                this.$refs.otpVerification?.resetCode();
            } catch (error) {
                const code = this.getErrorCode(error);
                if (code === 'FORM_CAPTCHA_INVALID') {
                    this.handleExpiredCaptcha();
                    return;
                }
                EventBus.$emit('ERROR', {
                    Message: this.getOtpErrorMessage(code, true),
                });
            } finally {
                this.otpResendLoading = false;
            }
        },
    },
};
